import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { permissionService } from '../services/permissionService';
import TimeoutModal from '../components/TimeoutModal';
import MFAModal from '../components/MFAModal';
import { authService } from '../services/authService';
import { UNAUTHORIZED_EVENT, apiFetch } from '../utils/apiFetch';
import { consumeKioskRoleQuery, setKioskRole, type KioskRole } from '../utils/kioskDisplaySync';

// Helper function to properly capitalize a name
function capitalizeName(name: string): string {
  if (!name) return '';
  return name
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Helper function to get the first name from a full name
function getFirstName(fullName: string): string {
  if (!fullName) return '';
  return fullName.trim().split(' ')[0];
}

interface User {
  _id: string;
  name: string;
  username: string;
  accessLevel: string;
  isKiosk?: boolean;
  employeeId: string;
  title: string;
  displayName: string; // The properly formatted name to display (preferredName or firstName)
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (usernameOrEmail: string, password: string) => Promise<void>;
  loginMfa?: (ticket: string, code: string, isRecovery?: boolean) => Promise<void>;
  logout: () => void;
  applyAccessToken: (token: string) => Promise<void>;
  permissions: string[];
  hasPermission: (key: string) => boolean;
  showTimeoutModal: boolean;
  resetTimeout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'lts_jwt_token';
const LAST_ACTIVITY_AT_KEY = 'lts_last_activity_at';
const KIOSK_REFRESH_KEY = 'lts_kiosk_refresh_token';
const KIOSK_DEVICE_AUTH_HASH = 'kioskDeviceAuth';

function normalizeInjectedKioskRole(raw: unknown): KioskRole | null {
  const v = String(raw || '').trim().toLowerCase();
  if (v === 'controller' || v === 'display' || v === 'regular') return v;
  return null;
}

/** Injected by kiosk-linux: #kioskDeviceAuth=<encodeURIComponent(JSON)> */
function consumeKioskDeviceAuthHash(): { accessToken: string; refreshToken: string; role: KioskRole | null } | null {
  if (typeof window === 'undefined') return null;
  const hash = String(window.location.hash || '').replace(/^#/, '');
  if (!hash) return null;
  const params = new URLSearchParams(hash.includes('=') ? hash : '');
  // Also support raw #kioskDeviceAuth=...
  let raw = params.get(KIOSK_DEVICE_AUTH_HASH);
  if (!raw && hash.startsWith(`${KIOSK_DEVICE_AUTH_HASH}=`)) {
    raw = hash.slice(`${KIOSK_DEVICE_AUTH_HASH}=`.length);
  }
  if (!raw) return null;
  try {
    const json = JSON.parse(decodeURIComponent(raw));
    const accessToken = typeof json?.accessToken === 'string' ? json.accessToken : '';
    const refreshToken = typeof json?.refreshToken === 'string' ? json.refreshToken : '';
    if (!accessToken || !refreshToken) return null;
    const role = normalizeInjectedKioskRole(json?.role);
    // Clear hash without reload
    const url = new URL(window.location.href);
    url.hash = '';
    window.history.replaceState(null, '', url.toString());
    return { accessToken, refreshToken, role };
  } catch {
    return null;
  }
}

function storeKioskSession(accessToken: string, refreshToken: string) {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(KIOSK_REFRESH_KEY, refreshToken);
  localStorage.setItem(LAST_ACTIVITY_AT_KEY, String(Date.now()));
}

function clearKioskRefresh() {
  localStorage.removeItem(KIOSK_REFRESH_KEY);
}

function getUserFromToken(token: string | null): User | null {
  if (!token) return null;
  try {
    const decoded: any = jwtDecode(token);
    // Reject expired tokens (jwtDecode does not validate exp)
    if (decoded.exp && decoded.exp * 1000 < Date.now()) {
      return null;
    }
    // Access tokens must be type access (or legacy missing type)
    if (decoded.type && decoded.type !== 'access') {
      return null;
    }
    const id = decoded.userId || decoded.deviceId;
    if (!id) return null;
    const name = decoded.name || '';
    return {
      _id: id,
      name: name,
      username: decoded.username,
      accessLevel: decoded.accessLevel,
      isKiosk: !!decoded.isKiosk,
      employeeId: decoded.employeeId || '',
      title: decoded.title || '',
      displayName: capitalizeName(getFirstName(name)) || decoded.username || 'User'
    };
  } catch {
    return null;
  }
}

// Helper to fetch contact info and update displayName
async function fetchDisplayName(employeeId: string, fallbackName: string, username: string): Promise<string> {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const res = await apiFetch(`/api/contacts/${encodeURIComponent(employeeId)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    const payload = await res.json().catch(() => ({}));
    const contact = payload?.data;
    if (!res.ok || !contact) throw new Error('Contact lookup failed');
    // Use preferredName if available, otherwise extract first name from full name
    if (contact.preferredName) {
      return capitalizeName(contact.preferredName);
    }
    if (contact.name) {
      return capitalizeName(getFirstName(contact.name));
    }
  } catch {
    // Contact not found or error - fall back to token data
  }
  // Fallback: use the name from token or username
  return capitalizeName(getFirstName(fallbackName)) || username || 'User';
}

function shouldLookupContact(user: Pick<User, 'employeeId' | 'isKiosk'> | null): boolean {
  if (!user?.employeeId) return false;
  if (user.isKiosk) return false;
  if (String(user.employeeId).startsWith('KIOSK-')) return false;
  return true;
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [mfaTicket, setMfaTicket] = useState<string | null>(null);
  const [mfaError, setMfaError] = useState<string | undefined>(undefined);
  const [mfaRequiresSetup, setMfaRequiresSetup] = useState<boolean>(false);
  const [mfaOtpauth, setMfaOtpauth] = useState<string | undefined>(undefined);
  const [mfaAvailableMethods, setMfaAvailableMethods] = useState<string[]>(['totp']);
  const [mfaSetupMethods, setMfaSetupMethods] = useState<string[]>(['totp', 'passkey']);
  const [mfaRememberDays, setMfaRememberDays] = useState<number>(7);
  const [sessionInactivityTimeoutMs, setSessionInactivityTimeoutMs] = useState<number>(60 * 60 * 1000);
  const [warningCountdownSeconds, setWarningCountdownSeconds] = useState<number>(60);
  const navigate = useNavigate();

  const TOKEN_TIMEOUT_MS = sessionInactivityTimeoutMs;
  const WARNING_COUNTDOWN_SECONDS = warningCountdownSeconds;
  const INACTIVITY_WARNING_MS = Math.max(0, TOKEN_TIMEOUT_MS - (WARNING_COUNTDOWN_SECONDS * 1000));
  
  const inactivityTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  const clearTimers = React.useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    inactivityTimerRef.current = null;
    warningTimerRef.current = null;
  }, []);

  const clearSessionState = React.useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(LAST_ACTIVITY_AT_KEY);
    clearKioskRefresh();
    setShowTimeoutModal(false);
    setUser(null);
    setPermissions([]);
  }, []);

  const forceLogout = React.useCallback((reason: string) => {
    clearTimers();
    clearSessionState();
    navigate('/login', { replace: true, state: { reason } });
  }, [clearSessionState, clearTimers, navigate]);

  const armTimersFromLastActivity = React.useCallback((lastActivityAt: number) => {
    if (!user) return;
    if (user.isKiosk) {
      clearTimers();
      setShowTimeoutModal(false);
      return;
    }
    clearTimers();
    const now = Date.now();
    const inactiveForMs = now - lastActivityAt;
    const warningInMs = INACTIVITY_WARNING_MS - inactiveForMs;
    const logoutInMs = TOKEN_TIMEOUT_MS - inactiveForMs;

    if (logoutInMs <= 0) {
      forceLogout('inactive');
      return;
    }

    if (warningInMs <= 0) {
      setShowTimeoutModal(true);
    } else {
      warningTimerRef.current = setTimeout(() => {
        setShowTimeoutModal(true);
      }, warningInMs);
    }

    inactivityTimerRef.current = setTimeout(() => {
      forceLogout('inactive');
    }, logoutInMs);
  }, [INACTIVITY_WARNING_MS, TOKEN_TIMEOUT_MS, clearTimers, forceLogout, user]);

  const getLastActivityAt = React.useCallback(() => {
    const raw = localStorage.getItem(LAST_ACTIVITY_AT_KEY);
    const parsed = raw ? Number(raw) : NaN;
    if (!Number.isFinite(parsed)) {
      const now = Date.now();
      localStorage.setItem(LAST_ACTIVITY_AT_KEY, String(now));
      return now;
    }
    return parsed;
  }, []);

  const reconcileSession = React.useCallback(() => {
    if (!user) return;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      forceLogout('session_expired');
      return;
    }

    try {
      const decoded: any = jwtDecode(token);
      if (decoded.exp && decoded.exp * 1000 <= Date.now()) {
        const refreshToken = localStorage.getItem(KIOSK_REFRESH_KEY);
        if (user.isKiosk && refreshToken) {
          void authService.kioskDeviceRefresh(refreshToken)
            .then((refreshed) => {
              storeKioskSession(refreshed.accessToken, refreshed.refreshToken);
              const nextUser = getUserFromToken(refreshed.accessToken);
              if (nextUser) setUser(nextUser);
            })
            .catch(() => forceLogout('session_expired'));
          return;
        }
        forceLogout('session_expired');
        return;
      }
      if (user.isKiosk) {
        clearTimers();
        setShowTimeoutModal(false);
        return;
      }
      const lastActivityAt = getLastActivityAt();
      armTimersFromLastActivity(lastActivityAt);
    } catch {
      forceLogout('session_expired');
    }
  }, [armTimersFromLastActivity, clearTimers, forceLogout, getLastActivityAt, user]);

  // Reset inactivity timers
  const resetTimers = React.useCallback(() => {
    clearTimers();
    setShowTimeoutModal(false);

    if (!user) return;
    if (user.isKiosk) return;
    const now = Date.now();
    localStorage.setItem(LAST_ACTIVITY_AT_KEY, String(now));
    armTimersFromLastActivity(now);
  }, [armTimersFromLastActivity, clearTimers, user]);

  // Handle staying logged in
  const handleStayLoggedIn = React.useCallback(() => {
    setShowTimeoutModal(false);
    resetTimers();
  }, [resetTimers]);

  // Handle logout from timeout
  const handleTimeoutLogout = React.useCallback(() => {
    forceLogout('timeout');
  }, [forceLogout]);

  // Global 401 handler: apiFetch dispatches this when backend returns 401
  useEffect(() => {
    const handleUnauthorized = () => {
      clearTimers();
      clearSessionState();
      navigate('/login', { replace: true, state: { reason: 'session_expired' } });
    };
    window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
  }, [clearSessionState, clearTimers, navigate]);

  // Set up event listeners for user activity
  useEffect(() => {
    if (!user) return;
    if (user.isKiosk) {
      clearTimers();
      setShowTimeoutModal(false);
      return;
    }
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];
    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        reconcileSession();
      }
    };

    events.forEach(event => window.addEventListener(event, resetTimers));
    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    resetTimers();

    return () => {
      events.forEach(event => window.removeEventListener(event, resetTimers));
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      clearTimers();
    };
  }, [clearTimers, reconcileSession, resetTimers, user]);

  // Helper to fetch permissions for current user (server enforces RBAC)
  const fetchPermissionsForCurrentUser = async () => {
    try {
      return await permissionService.getMine();
    } catch {
      return [];
    }
  };

  useEffect(() => {
    const initializeUser = async () => {
      try {
        // Prefer role from device-auth hash; always also consume ?kioskRole= (strips query).
        const injected = consumeKioskDeviceAuthHash();
        if (injected) {
          storeKioskSession(injected.accessToken, injected.refreshToken);
          if (injected.role) setKioskRole(injected.role);
        }
        consumeKioskRoleQuery();

        let token = localStorage.getItem(TOKEN_KEY);
        let u = getUserFromToken(token);

        // Access expired but kiosk refresh may still be valid
        if (!u) {
          const refreshToken = localStorage.getItem(KIOSK_REFRESH_KEY);
          if (refreshToken) {
            try {
              const refreshed = await authService.kioskDeviceRefresh(refreshToken);
              storeKioskSession(refreshed.accessToken, refreshed.refreshToken);
              token = refreshed.accessToken;
              u = getUserFromToken(token);
            } catch {
              clearKioskRefresh();
              localStorage.removeItem(TOKEN_KEY);
            }
          } else {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(LAST_ACTIVITY_AT_KEY);
          }
        }

        if (u) {
          if (shouldLookupContact(u)) {
            const displayName = await fetchDisplayName(u.employeeId, u.name, u.username);
            u.displayName = displayName;
          }
          setUser(u);
          fetchPermissionsForCurrentUser().then(setPermissions);
        }
      } finally {
        setLoading(false);
      }
    };
    initializeUser();
  }, []);

  // Proactively refresh kiosk device access tokens before expiry
  useEffect(() => {
    if (!user?.isKiosk) return;
    const refreshToken = localStorage.getItem(KIOSK_REFRESH_KEY);
    if (!refreshToken) return;

    let cancelled = false;
    let timer: number | null = null;

    const schedule = () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return;
      try {
        const decoded: any = jwtDecode(token);
        const expMs = Number(decoded?.exp) * 1000;
        if (!Number.isFinite(expMs)) return;
        // Refresh ~5 minutes before expiry, with per-browser jitter so dual
        // kiosk profiles don't hit /refresh in the same second.
        const roleHint =
          typeof sessionStorage !== 'undefined'
            ? String(sessionStorage.getItem('ltsKioskRole') || '')
            : ''
        const roleBias =
          roleHint === 'display' ? 20_000 : roleHint === 'controller' ? 0 : 10_000
        const jitterMs = roleBias + Math.floor(Math.random() * 40_000)
        const delay = Math.max(5_000, expMs - Date.now() - 5 * 60_000 + jitterMs);
        timer = window.setTimeout(async () => {
          const currentRefresh = localStorage.getItem(KIOSK_REFRESH_KEY);
          if (!currentRefresh || cancelled) return;
          try {
            const refreshed = await authService.kioskDeviceRefresh(currentRefresh);
            if (cancelled) return;
            storeKioskSession(refreshed.accessToken, refreshed.refreshToken);
            const nextUser = getUserFromToken(refreshed.accessToken);
            if (nextUser) setUser(nextUser);
            schedule();
          } catch {
            if (!cancelled) forceLogout('session_expired');
          }
        }, delay);
      } catch {
        /* ignore */
      }
    };

    schedule();
    return () => {
      cancelled = true;
      if (timer != null) window.clearTimeout(timer);
    };
  }, [user?.isKiosk, forceLogout]);

  useEffect(() => {
    authService.getAuthConfig()
      .then(cfg => {
        if (typeof cfg?.mfaRememberDays === 'number' && cfg.mfaRememberDays > 0) {
          setMfaRememberDays(cfg.mfaRememberDays);
        }
        const inactivityMinutes = Number(cfg?.sessionInactivityTimeoutMinutes);
        if (Number.isFinite(inactivityMinutes) && inactivityMinutes > 0) {
          setSessionInactivityTimeoutMs(inactivityMinutes * 60 * 1000);
        }
        const warningSeconds = Number(cfg?.sessionWarningCountdownSeconds);
        if (Number.isFinite(warningSeconds) && warningSeconds > 0) {
          setWarningCountdownSeconds(warningSeconds);
        }
      })
      .catch(() => {
        setMfaRememberDays(7);
        setSessionInactivityTimeoutMs(60 * 60 * 1000);
        setWarningCountdownSeconds(60);
      });
  }, []);

  const login = async (usernameOrEmail: string, password: string) => {
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: usernameOrEmail, email: usernameOrEmail, password })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Login failed');
      if (typeof data?.mfaRememberDays === 'number' && data.mfaRememberDays > 0) {
        setMfaRememberDays(data.mfaRememberDays);
      }
      if ((data?.requiresMfa || data?.requiresMfaSetup) && data?.ticket) {
        const rememberToken = authService.getRememberDeviceToken();
        if (!data?.requiresMfaSetup && rememberToken) {
          try {
            const trusted = await authService.trustedDeviceLogin(data.ticket, rememberToken);
            localStorage.setItem(TOKEN_KEY, trusted.token);
            localStorage.setItem(LAST_ACTIVITY_AT_KEY, String(Date.now()));
            const u = getUserFromToken(trusted.token);
            if (u && shouldLookupContact(u)) u.displayName = await fetchDisplayName(u.employeeId, u.name, u.username);
            setUser(u);
            setPermissions(await fetchPermissionsForCurrentUser());
            return;
          } catch (e: any) {
            const msg = String(e?.message || '');
            // Only clear remember token when backend explicitly rejects it (401); keep it on network errors so user can retry
            if (msg.includes('Invalid') || msg.includes('expired') || msg.includes('mismatch')) {
              authService.setRememberDeviceToken(null);
            }
          }
        }
        setMfaTicket(data.ticket);
        setMfaRequiresSetup(!!data?.requiresMfaSetup);
        setMfaAvailableMethods(Array.isArray(data?.availableMfaMethods) && data.availableMfaMethods.length ? data.availableMfaMethods : ['totp']);
        setMfaSetupMethods(['totp', 'passkey']);
        if (data?.requiresMfaSetup) {
          const s = await authService.mfaSetupStart(data.ticket).catch(() => null);
          setMfaOtpauth(s?.otpauth);
        }
        return; // wait for MFA step
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(LAST_ACTIVITY_AT_KEY, String(Date.now()));
      // Get user from token and fetch proper display name
      const u = getUserFromToken(data.token);
      if (u && shouldLookupContact(u)) {
        u.displayName = await fetchDisplayName(u.employeeId, u.name, u.username);
      }
      setUser(u);
      // Fetch permissions for the new user
      const perms = await fetchPermissionsForCurrentUser();
      setPermissions(perms);
    } finally {
      setLoading(false);
    }
  };

  const loginMfa = async (ticket: string, code: string, isRecovery?: boolean, rememberDevice?: boolean) => {
    setLoading(true);
    setMfaError(undefined);
    try {
      let token: string;
      let rememberDeviceToken: string | undefined;
      if (mfaRequiresSetup) {
        const setupRes = await authService.mfaSetupVerify(code, ticket);
        if (!setupRes?.token) throw new Error('MFA setup did not complete');
        token = setupRes.token;
      } else {
        const verifyRes = await authService.mfaVerify(ticket, isRecovery ? undefined : code, isRecovery ? code : undefined, rememberDevice);
        token = verifyRes.token;
        rememberDeviceToken = verifyRes.rememberDeviceToken;
      }
      if (rememberDeviceToken) authService.setRememberDeviceToken(rememberDeviceToken);
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(LAST_ACTIVITY_AT_KEY, String(Date.now()));
      // Get user from token and fetch proper display name
      const u = getUserFromToken(token);
      if (u && shouldLookupContact(u)) {
        u.displayName = await fetchDisplayName(u.employeeId, u.name, u.username);
      }
      setUser(u);
      setMfaTicket(null);
      setMfaRequiresSetup(false);
      setMfaOtpauth(undefined);
      setMfaAvailableMethods(['totp']);
      const perms = await fetchPermissionsForCurrentUser();
      setPermissions(perms);
    } catch (e: any) {
      setMfaError(e?.message || 'MFA verification failed');
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const loginMfaPasskey = async (ticket: string, rememberDevice?: boolean) => {
    setLoading(true);
    setMfaError(undefined);
    try {
      let token: string | undefined;
      let rememberDeviceToken: string | undefined;
      if (mfaRequiresSetup) {
        const setupRes = await authService.mfaPasskeyRegister(ticket);
        token = setupRes.token;
      } else {
        const verifyRes = await authService.mfaPasskeyAuthenticate(ticket, rememberDevice);
        token = verifyRes.token;
        rememberDeviceToken = verifyRes.rememberDeviceToken;
      }
      if (!token) throw new Error('Passkey verification failed');
      if (rememberDeviceToken) authService.setRememberDeviceToken(rememberDeviceToken);
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(LAST_ACTIVITY_AT_KEY, String(Date.now()));
      const u = getUserFromToken(token);
      if (u && shouldLookupContact(u)) {
        u.displayName = await fetchDisplayName(u.employeeId, u.name, u.username);
      }
      setUser(u);
      setMfaTicket(null);
      setMfaRequiresSetup(false);
      setMfaOtpauth(undefined);
      setMfaAvailableMethods(['totp']);
      const perms = await fetchPermissionsForCurrentUser();
      setPermissions(perms);
    } catch (e: any) {
      setMfaError(e?.message || 'Passkey verification failed');
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const applyAccessToken = async (token: string) => {
    const next = String(token || '').trim()
    if (!next) return
    localStorage.setItem(TOKEN_KEY, next)
    localStorage.setItem(LAST_ACTIVITY_AT_KEY, String(Date.now()))
    const u = getUserFromToken(next)
    if (u && shouldLookupContact(u)) {
      u.displayName = await fetchDisplayName(u.employeeId, u.name, u.username)
    }
    setUser(u)
  }

  const logout = () => {
    clearTimers();
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(LAST_ACTIVITY_AT_KEY);
    clearKioskRefresh();
    // Keep remember-device token so trusted-device MFA persists until backend expiry.
    setUser(null);
    setPermissions([]);
    navigate('/login', { replace: true });
  };

  const hasPermission = (key: string) => permissions.includes(key);

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated: !!user, 
      loading, 
      login, 
      loginMfa,
      logout,
      applyAccessToken, 
      permissions, 
      hasPermission,
      showTimeoutModal,
      resetTimeout: resetTimers
    }}>
      {children}
      <MFAModal
        isOpen={!!mfaTicket}
        error={mfaError}
        requiresSetup={mfaRequiresSetup}
        availableMethods={mfaAvailableMethods}
        setupMethods={mfaSetupMethods}
        rememberDays={mfaRememberDays}
        otpauth={mfaOtpauth}
        onSetup={(c) => mfaTicket && loginMfa(mfaTicket, c)}
        onSetupPasskey={() => mfaTicket && loginMfaPasskey(mfaTicket)}
        onVerify={(c, remember) => mfaTicket && loginMfa(mfaTicket, c, false, remember)}
        onVerifyPasskey={(remember) => mfaTicket && loginMfaPasskey(mfaTicket, remember)}
        onUseRecovery={(c, remember) => mfaTicket && loginMfa(mfaTicket, c, true, remember)}
        onClose={() => { setMfaTicket(null); setMfaRequiresSetup(false); setMfaOtpauth(undefined); setMfaAvailableMethods(['totp']); }}
      />
      <TimeoutModal
        isOpen={showTimeoutModal}
        onStayLoggedIn={handleStayLoggedIn}
        onLogout={handleTimeoutLogout}
        countdownSeconds={WARNING_COUNTDOWN_SECONDS}
      />
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
} 